import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { TooltipButton } from 'Components/User/DevoteeUser.style';
import React from 'react';

/**
 * @component
 * @description Individual menu card component for sidebar navigation items.
 * Renders menu items with support for expandable submenus, active state indication,
 * and professional styling with smooth animations.
 *
 * Key Features:
 * - **Expandable Submenus**: Support for nested menu items with expand/collapse functionality
 * - **Active State Indication**: Visual highlighting of currently selected menu items
 * - **Smooth Animations**: Professional transitions for hover, selection, and expand/collapse
 * - **Responsive Design**: Adapts to sidebar open/closed states
 * - **Professional Styling**: Consistent theming with hover effects and visual feedback
 * - **Tooltip Support**: Shows menu names when sidebar is collapsed
 * - **Navigation Integration**: Seamless integration with React Router
 * - **Accessibility**: Proper ARIA attributes and keyboard navigation support
 *
 * Visual Features:
 * - **Active Menu Highlighting**: Golden accent color for selected items
 * - **Hover Effects**: Subtle animations and color changes on hover
 * - **Left Border Accent**: Colored left border for active items
 * - **Icon Styling**: Consistent icon sizing and coloring
 * - **Text Overflow**: Proper text truncation with ellipsis
 * - **Drop Shadow Effects**: Subtle shadows for enhanced visual depth
 *
 * Interaction Features:
 * - **Click Handling**: Expands submenus or navigates to routes
 * - **Submenu Toggle**: Toggles submenu visibility on parent menu click
 * - **Direct Navigation**: Navigates directly for menu items without submenus
 * - **State Management**: Maintains submenu open/closed state
 *
 * @param {Object} props - Component props
 * @param {Object} props.menus - Menu item configuration object
 * @param {string} props.menus.name - Display name of the menu item
 * @param {string} props.menus.path - Route path for navigation
 * @param {React.Component} props.menus.icon - Icon component for the menu item
 * @param {Array} [props.menus.submenu] - Array of submenu items
 * @param {Object} [props.menus.state] - Navigation state to pass to router
 * @param {boolean} props.open - Whether the sidebar is in expanded state
 * @param {number|null} props.currentSubMenuId - Currently expanded submenu ID
 * @param {Function} props.setCurrentSubmenuId - Function to set current submenu ID
 * @param {number} props.index - Index of this menu item in the menu list
 * @param {Function} props.navigate - React Router navigate function
 * @param {string} props.iconColor - Color for menu icons
 * @param {boolean} [props.hideMenu=false] - Whether to hide this menu item
 *
 * @returns {JSX.Element} Rendered menu card with optional submenu
 *
 * @example
 * // Basic menu item without submenu
 * <MenuCard
 *   menus={{
 *     name: "Dashboard",
 *     path: "/dashboard",
 *     icon: DashboardIcon
 *   }}
 *   open={true}
 *   currentSubMenuId={null}
 *   setCurrentSubmenuId={setCurrentSubmenuId}
 *   index={0}
 *   navigate={navigate}
 *   iconColor="#e0eeff"
 * />
 *
 * @example
 * // Menu item with submenu
 * <MenuCard
 *   menus={{
 *     name: "User Management",
 *     path: "/users",
 *     icon: UsersIcon,
 *     submenu: [
 *       { name: "Devotees", path: "/users/devotees", icon: UserIcon },
 *       { name: "Priests", path: "/users/priests", icon: PriestIcon }
 *     ]
 *   }}
 *   open={true}
 *   currentSubMenuId={1}
 *   setCurrentSubmenuId={setCurrentSubmenuId}
 *   index={1}
 *   navigate={navigate}
 *   iconColor="#e0eeff"
 * />
 *
 * @example
 * // Hidden menu item
 * <MenuCard
 *   menus={menuConfig}
 *   open={true}
 *   currentSubMenuId={null}
 *   setCurrentSubmenuId={setCurrentSubmenuId}
 *   index={2}
 *   navigate={navigate}
 *   iconColor="#e0eeff"
 *   hideMenu={true}
 * />
 *
 * @see TooltipButton - Custom tooltip button component
 * @see Collapse - Material-UI collapse component for submenu animation
 * @see ListItemButton - Material-UI list item button component
 * @see ExpandLess - Material-UI expand less icon
 * @see ExpandMore - Material-UI expand more icon
 */
const MenuCard = React.memo(
    ({
        menus,
        open,
        currentSubMenuId,
        setCurrentSubmenuId,
        index,
        navigate,
        iconColor,
        hideMenu = false
    }) => {
        /**
         * @constant
         * @description Determines if the main menu item is currently selected.
         * Compares the current location pathname with the menu item's path.
         *
         * @type {boolean}
         */
        const isMainMenuSelected = location.pathname === menus.path;

        /**
         * @constant
         * @description Determines if the submenu is currently expanded.
         * Compares the current submenu ID with this menu item's index.
         *
         * @type {boolean}
         */
        const isSubmenuOpen = index === currentSubMenuId;

        /**
         * @constant
         * @description Styling configuration for main menu items.
         * Includes hover effects, active state styling, and smooth transitions.
         *
         * Features:
         * - Smooth transitions for all state changes
         * - Active state background with golden accent
         * - Rounded corners on the right side
         * - Left border accent for active items
         * - Hover effects with subtle animations
         * - Responsive padding based on sidebar state
         *
         * @type {Object}
         */
        const menuItemStyles = {
            transition: 'all 0.3s ease',
            backgroundColor: isMainMenuSelected ? 'rgba(245, 224, 181, 0.15)' : 'transparent',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            margin: '2px 0',
            padding: open ? '8px 16px' : '8px 8px',
            minHeight: '40px',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '4px',
                backgroundColor: isMainMenuSelected ? 'rgb(245, 224, 181)' : 'transparent',
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                transition: 'all 0.3s ease'
            },
            '&:hover': {
                backgroundColor: isMainMenuSelected
                    ? 'rgba(245, 224, 181, 0.2)'
                    : 'rgba(255, 255, 255, 0.08)',
                transform: 'translateX(4px)',
                '&::before': {
                    backgroundColor: isMainMenuSelected
                        ? 'rgb(245, 224, 181)'
                        : 'rgba(245, 224, 181, 0.5)'
                }
            },
            '&.Mui-selected': {
                backgroundColor: 'rgba(245, 224, 181, 0.15)',
                '&:hover': {
                    backgroundColor: 'rgba(245, 224, 181, 0.2)'
                }
            }
        };

        /**
         * @constant
         * @description Styling configuration for submenu items.
         * Similar to main menu styling but with reduced dimensions and different spacing.
         *
         * Features:
         * - Smaller dimensions than main menu items
         * - Subtle hover effects
         * - Left border accent for active submenu items
         * - Smooth transitions
         * - Responsive text wrapping
         *
         * @type {Object}
         */
        const subMenuItemStyles = {
            textWrap: 'auto',
            transition: 'all 0.3s ease',
            backgroundColor: 'transparent',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            margin: '1px 0',
            padding: open ? '6px 16px' : '6px 8px',
            minHeight: '36px',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '3px',
                backgroundColor: 'transparent',
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                transition: 'all 0.3s ease'
            },
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                transform: 'translateX(4px)',
                '&::before': {
                    backgroundColor: 'rgba(245, 224, 181, 0.5)'
                }
            },
            '&.Mui-selected': {
                backgroundColor: 'rgba(245, 224, 181, 0.12)',
                '&:hover': {
                    backgroundColor: 'rgba(245, 224, 181, 0.17)'
                }
            }
        };

        /**
         * @function
         * @description Handles menu item click events.
         * Either toggles submenu visibility or navigates to the menu route.
         *
         * Logic:
         * - If menu has submenu items, toggles submenu open/closed state
         * - If menu has no submenu, navigates to the menu path
         * - Passes navigation state if available
         *
         * @returns {void}
         */
        const handleClick = () => {
            if (menus?.submenu?.length > 0) {
                if (currentSubMenuId !== null && currentSubMenuId === index) {
                    setCurrentSubmenuId(null);
                } else {
                    setCurrentSubmenuId(index);
                }
            } else {
                navigate(menus.path, { state: menus?.state });
            }
        };

        /**
         * @constant
         * @description Styling configuration for menu icons.
         * Includes active state coloring, responsive sizing, and visual effects.
         *
         * Features:
         * - Active state golden color
         * - Responsive sizing based on sidebar state
         * - Smooth transitions
         * - Drop shadow effects for active items
         *
         * @type {Object}
         */
        const iconStyles = {
            color: isMainMenuSelected ? 'rgb(245, 224, 181)' : iconColor,
            fontSize: open ? '1.3rem' : '1.1rem',
            transition: 'all 0.3s ease',
            filter: isMainMenuSelected ? 'drop-shadow(0 2px 2px rgba(245, 224, 181, 0.3))' : 'none'
        };

        /**
         * @constant
         * @description Styling configuration for menu text.
         * Includes responsive visibility, active state styling, and text overflow handling.
         *
         * Features:
         * - Responsive opacity based on sidebar state
         * - Active state golden color
         * - Text overflow handling with ellipsis
         * - Font weight changes for active items
         * - Text shadow effects
         *
         * @type {Object}
         */
        const textStyles = {
            opacity: open ? 1 : 0,
            transition: 'opacity 0.3s ease',
            color: isMainMenuSelected ? 'rgb(245, 224, 181)' : iconColor,
            '& .MuiTypography-root': {
                fontSize: open ? '0.9rem' : '0.85rem',
                fontWeight: isMainMenuSelected ? 600 : 400,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textShadow: isMainMenuSelected ? '0 1px 1px rgba(245, 224, 181, 0.2)' : 'none'
            }
        };

        return (
            <>
                <ListItem
                    disablePadding
                    key={menus.name}
                    sx={{
                        display: hideMenu && 'none',
                        width: '100%'
                    }}>
                    <TooltipButton
                        disableRipple
                        onClick={handleClick}
                        title={!open ? menus.name : ''}
                        style={{
                            width: '100%',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                        <ListItemButton selected={isMainMenuSelected} sx={menuItemStyles}>
                            <ListItemIcon
                                sx={{
                                    minWidth: open ? '36px' : '28px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                <menus.icon sx={iconStyles} />
                            </ListItemIcon>
                            <ListItemText primary={menus.name} sx={textStyles} />

                            {menus?.submenu?.length > 0 &&
                                open &&
                                (currentSubMenuId === index ? (
                                    <ExpandLess
                                        sx={{
                                            color: iconColor,
                                            fontSize: '1.1rem',
                                            transition: 'transform 0.3s ease',
                                            ml: 1,
                                            filter: isMainMenuSelected
                                                ? 'drop-shadow(0 2px 2px rgba(245, 224, 181, 0.3))'
                                                : 'none'
                                        }}
                                    />
                                ) : (
                                    <ExpandMore
                                        sx={{
                                            color: iconColor,
                                            fontSize: '1.1rem',
                                            transition: 'transform 0.3s ease',
                                            ml: 1,
                                            filter: isMainMenuSelected
                                                ? 'drop-shadow(0 2px 2px rgba(245, 224, 181, 0.3))'
                                                : 'none'
                                        }}
                                    />
                                ))}
                        </ListItemButton>
                    </TooltipButton>
                </ListItem>
                <Collapse in={isSubmenuOpen} timeout="auto" sx={{ width: '100%' }}>
                    {menus?.submenu?.map((submenus) => {
                        const isSubmenuSelected = location.pathname === submenus.path;
                        return (
                            <List
                                component="div"
                                disablePadding
                                key={submenus.name}
                                sx={{ width: '100%' }}>
                                <TooltipButton
                                    disableRipple
                                    onClick={() =>
                                        navigate(submenus.path, { state: submenus?.state })
                                    }
                                    title={!open ? submenus.name : ''}
                                    style={{
                                        width: '100%',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <ListItemButton
                                        disabled={submenus.disable}
                                        sx={{
                                            ...subMenuItemStyles,
                                            pl: !open ? 2 : 4,
                                            backgroundColor: isSubmenuSelected
                                                ? 'rgba(245, 224, 181, 0.12)'
                                                : 'transparent',
                                            '&::before': {
                                                ...subMenuItemStyles['&::before'],
                                                backgroundColor: isSubmenuSelected
                                                    ? 'rgb(245, 224, 181)'
                                                    : 'transparent'
                                            }
                                        }}
                                        selected={isSubmenuSelected}>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: open ? '32px' : '24px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                            <submenus.icon
                                                sx={{
                                                    color: isSubmenuSelected
                                                        ? 'rgb(245, 224, 181)'
                                                        : iconColor,
                                                    fontSize: open ? '1.1rem' : '1rem',
                                                    transition: 'all 0.3s ease',
                                                    filter: isSubmenuSelected
                                                        ? 'drop-shadow(0 2px 2px rgba(245, 224, 181, 0.3))'
                                                        : 'none'
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={submenus.name}
                                            sx={{
                                                opacity: open ? 1 : 0,
                                                transition: 'opacity 0.3s ease',
                                                color: isSubmenuSelected
                                                    ? 'rgb(245, 224, 181)'
                                                    : iconColor,
                                                '& .MuiTypography-root': {
                                                    fontSize: open ? '0.85rem' : '0.8rem',
                                                    fontWeight: isSubmenuSelected ? 600 : 400,
                                                    transition: 'all 0.3s ease',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    textShadow: isSubmenuSelected
                                                        ? '0 1px 1px rgba(245, 224, 181, 0.2)'
                                                        : 'none'
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                </TooltipButton>
                            </List>
                        );
                    })}
                </Collapse>
            </>
        );
    }
);

MenuCard.displayName = 'MenuCard';

export default MenuCard;
